import { Check, Loader2, MapPin, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ProfileBadge } from "@/components/campaigns/ProfileBadge";
import { Button } from "@/components/ui/button";
import { isAcceptedFile } from "@/lib/file-utils";

/**
 * ConfirmationCard — shows campaign details for user to confirm before creation.
 */
interface ConfirmationCardProps {
  name: string;
  goal?: string;
  profileName?: string;
  profileDescription?: string;
  onConfirm: () => void;
  onEdit: () => void;
  confirmed: boolean;
}

export function ConfirmationCard({
  name,
  goal,
  profileName,
  profileDescription,
  onConfirm,
  onEdit,
  confirmed,
}: ConfirmationCardProps) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 max-w-md">
      <div className="space-y-2">
        <div>
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Campaign Name
          </span>
          <p className="text-sm font-medium">{name}</p>
        </div>
        {goal && (
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Goal
            </span>
            <p className="text-sm">{goal}</p>
          </div>
        )}
        {profileName && (
          <div>
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Profile
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              <ProfileBadge name={profileName} />
              {profileDescription && (
                <span className="text-xs text-muted-foreground">{profileDescription}</span>
              )}
            </div>
          </div>
        )}
      </div>
      {confirmed ? (
        <div className="flex items-center gap-1.5 text-sm text-score-high">
          <Check className="h-4 w-4" />
          Campaign created
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={onConfirm}>
            Looks good, start!
          </Button>
          <Button size="sm" variant="ghost" onClick={onEdit}>
            Edit
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * UploadCard — inline file upload drop zone in the chat.
 */
interface UploadCardProps {
  campaignId: string;
  onUploaded: () => void;
}

export function UploadCard({ campaignId, onUploaded }: UploadCardProps) {
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    if (!isAcceptedFile(file)) {
      toast.error("Please upload a CSV, XLSX, or XLS file");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`/api/campaigns/${campaignId}/upload`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Upload failed");
      }
      setUploaded(true);
      onUploaded();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function handleSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  if (uploaded) {
    return (
      <div className="flex items-center gap-1.5 text-sm text-score-high rounded-xl border border-border bg-card p-4">
        <Check className="h-4 w-4" />
        File uploaded — processing started
      </div>
    );
  }

  return (
    <div className="max-w-md">
      <button
        type="button"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-6 cursor-pointer hover:border-primary/50 transition-colors disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Uploading...</p>
          </>
        ) : (
          <>
            <Upload className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Drag & drop a CSV or XLSX file, or click to browse
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleSelect}
          className="hidden"
        />
      </button>
    </div>
  );
}

/**
 * ProgressCard — shows geocoding and scoring progress inline.
 */
interface ProgressCardProps {
  campaignId: string;
  profileId: string;
  onComplete: () => void;
}

export function ProgressCard({ campaignId, profileId, onComplete }: ProgressCardProps) {
  const [geocoding, setGeocoding] = useState({
    total: 0,
    done: 0,
    phase: "geocoding" as "geocoding" | "scoring" | "complete",
  });
  const completedRef = useRef(false);
  const phaseRef = useRef(geocoding.phase);
  phaseRef.current = geocoding.phase;
  const scoringTriggeredRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    let interval: ReturnType<typeof setInterval>;

    async function poll() {
      if (completedRef.current) return;
      try {
        const res = await fetch(`/api/campaigns/${campaignId}/geocode-status`, {
          credentials: "include",
        });
        if (!res.ok || !mounted) return;
        const data = await res.json();

        const total = data.total ?? 0;
        const done = (data.success ?? 0) + (data.failed ?? 0) + (data.ambiguous ?? 0);
        const geocodeDone = data.pending === 0 && total > 0;

        if (geocodeDone && phaseRef.current === "geocoding") {
          setGeocoding({ total, done, phase: "scoring" });
          // Trigger scoring once
          if (!scoringTriggeredRef.current) {
            scoringTriggeredRef.current = true;
            fetch(`/api/campaigns/${campaignId}/score`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ profileId }),
            }).catch(console.error);
          }
        } else if (!geocodeDone) {
          setGeocoding((prev) => ({ ...prev, total, done }));
        }
      } catch {
        /* ignore polling errors */
      }

      // Check scoring status
      if (phaseRef.current === "scoring") {
        try {
          const res = await fetch(`/api/campaigns/${campaignId}/scoring-status`, {
            credentials: "include",
          });
          if (!res.ok || !mounted) return;
          const data = await res.json();
          if (data.pending === 0 && data.total > 0 && !completedRef.current) {
            completedRef.current = true;
            setGeocoding((prev) => ({ ...prev, phase: "complete" }));
            onComplete();
            clearInterval(interval);
          }
        } catch {
          /* ignore */
        }
      }
    }

    interval = setInterval(poll, 2000);
    poll(); // Initial poll

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [campaignId, profileId, onComplete]);

  const progress = geocoding.total > 0 ? Math.round((geocoding.done / geocoding.total) * 100) : 0;

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3 max-w-md">
      {/* Geocoding progress */}
      <div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            Geocoding addresses
          </span>
          <span>
            {geocoding.phase === "geocoding" ? `${geocoding.done}/${geocoding.total}` : "Done"}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${geocoding.phase === "geocoding" ? progress : 100}%` }}
          />
        </div>
      </div>

      {/* Scoring progress */}
      {(geocoding.phase === "scoring" || geocoding.phase === "complete") && (
        <div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Scoring locations</span>
            <span>{geocoding.phase === "complete" ? "Done" : "Processing..."}</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${geocoding.phase === "complete" ? "bg-score-high w-full" : "bg-primary w-2/3 animate-pulse"}`}
            />
          </div>
        </div>
      )}

      {geocoding.phase === "complete" && (
        <div className="flex items-center gap-1.5 text-sm text-score-high">
          <Check className="h-4 w-4" />
          All locations scored
        </div>
      )}
    </div>
  );
}

/**
 * ViewResultsCard — button to transition from creation chat to results view.
 */
interface ViewResultsCardProps {
  campaignId: string;
  onViewResults: (campaignId: string) => void;
}

export function ViewResultsCard({ campaignId, onViewResults }: ViewResultsCardProps) {
  return (
    <div className="max-w-md">
      <Button onClick={() => onViewResults(campaignId)} className="gap-2">
        View Results
      </Button>
    </div>
  );
}
